import { z } from 'zod';

import { jsonRequest } from './shared';

const cacheClearResponseSchema = z.object({
  ok: z.boolean(),
  timestamp: z.string(),
  cleared: z
    .array(
      z.object({
        name: z.string(),
        scope: z.string().optional(),
        tenantId: z.string().optional(),
      }),
    )
    .default([]),
  message: z.string().optional(),
});

const diagnosticsResponseSchema = z.object({
  ok: z.boolean(),
  timestamp: z.string(),
  metrics: z.object({
    dbLatencyMs: z.number(),
    pendingPrinterJobs: z.number(),
    failedPrinterJobs: z.number(),
    offlinePrinters: z.number(),
    lastPrinterSeenAt: z.number().nullable(),
    uptimeSeconds: z.number(),
  }),
  issues: z.array(z.string()).default([]),
  recommendations: z.array(z.string()).default([]),
});

const updateCheckResponseSchema = z.object({
  ok: z.boolean(),
  checkedAt: z.string(),
  currentVersion: z.string(),
  currentCommit: z.string().nullable(),
  currentBuiltAt: z.string(),
  latestVersion: z.string(),
  latestReleaseDate: z.string(),
  releaseNotes: z.array(z.string()).default([]),
  updateAvailable: z.boolean(),
});

const securityOverviewResponseSchema = z.object({
  ok: z.boolean(),
  checkedAt: z.string(),
  passwordCoverage: z.object({
    totalUsers: z.number(),
    withPassword: z.number(),
    coveragePct: z.number(),
  }),
  pinCoverage: z.object({
    totalStaff: z.number(),
    withPin: z.number(),
    recentlyRotated: z.number(),
    lastRotationAt: z.string().nullable(),
  }),
  privacyRequests: z.object({
    open: z.number(),
    completed: z.number(),
    lastRequestAt: z.string().nullable(),
  }),
  recentSecurityEvents: z.object({
    total: z.number(),
    lastEventAt: z.string().nullable(),
  }),
});

export type SystemCacheClearResponse = z.infer<typeof cacheClearResponseSchema>;
export type SystemDiagnosticsResponse = z.infer<typeof diagnosticsResponseSchema>;
export type SystemUpdateCheckResponse = z.infer<typeof updateCheckResponseSchema>;
export type SecurityOverviewResponse = z.infer<typeof securityOverviewResponseSchema>;

export const clearSystemCaches = (signal?: AbortSignal) =>
  jsonRequest<SystemCacheClearResponse>('/settings/system/cache/clear', {
    method: 'POST',
    schema: cacheClearResponseSchema,
    signal,
  });

export const runSystemDiagnostics = (signal?: AbortSignal) =>
  jsonRequest<SystemDiagnosticsResponse>('/settings/system/diagnostics', {
    method: 'POST',
    schema: diagnosticsResponseSchema,
    signal,
  });

export const checkForSystemUpdates = (signal?: AbortSignal) =>
  jsonRequest<SystemUpdateCheckResponse>('/settings/system/update-check', {
    schema: updateCheckResponseSchema,
    signal,
  });

export const fetchSecurityOverview = (signal?: AbortSignal) =>
  jsonRequest<SecurityOverviewResponse>('/settings/security/overview', {
    schema: securityOverviewResponseSchema,
    signal,
  });
